import { apiService } from "./api";

export interface Notification {
  _id: string;
  userId: string;
  companyId?: string;
  type: string;
  title: string;
  message: string;
  priority: "low" | "medium" | "high" | "urgent";
  isRead: boolean;
  readAt?: string;
  relatedEntities: {
    contactId?: string;
    messageId?: string;
    templateId?: string;
    whatsAppNumberId?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface NotificationStats {
  unreadCount: number;
  byType: {
    message: number;
    system: number;
    user: number;
  };
  total: number;
}

export interface NotificationResponse {
  success: boolean;
  data: {
    notifications: Notification[];
    unreadCount: number;
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
  };
}

export interface NotificationCountResponse {
  success: boolean;
  data: {
    unreadCount: number;
  };
}

export interface NotificationStatsResponse {
  success: boolean;
  data: NotificationStats;
}

class NotificationService {
  /**
   * Get notifications for the authenticated user
   */
  async getNotifications(params?: {
    page?: number;
    limit?: number;
    type?: string;
    priority?: string;
    unreadOnly?: boolean;
  }): Promise<NotificationResponse> {
    const response = await apiService.get<NotificationResponse>(
      "/notifications",
      {
        params,
      }
    );
    return response.data;
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<number> {
    const response = await apiService.get<NotificationCountResponse>(
      "/notifications/count"
    );
    return response.data.data.unreadCount;
  }

  /**
   * Get notification statistics
   */
  async getStats(): Promise<NotificationStats> {
    const response = await apiService.get<NotificationStatsResponse>(
      "/notifications/stats"
    );
    return response.data.data;
  }

  /**
   * Get notifications by type
   */
  async getNotificationsByType(type: string): Promise<NotificationResponse> {
    const response = await apiService.get<NotificationResponse>(
      `/notifications/type/${type}`
    );
    return response.data;
  }

  /**
   * Get notifications by priority
   */
  async getNotificationsByPriority(
    priority: string
  ): Promise<NotificationResponse> {
    const response = await apiService.get<NotificationResponse>(
      `/notifications/priority/${priority}`
    );
    return response.data;
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId: string): Promise<void> {
    await apiService.put(`/notifications/${notificationId}/read`);
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    await apiService.put("/notifications/read-all");
  }

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId: string): Promise<void> {
    await apiService.delete(`/notifications/${notificationId}`);
  }

  /**
   * Create a test notification (for development/testing)
   */
  async createTestNotification(data: {
    title: string;
    message: string;
    type?: string;
    priority?: string;
  }): Promise<Notification> {
    const response = await apiService.post("/notifications/test", data);
    return response.data.data;
  }

  /**
   * Clean up expired notifications (admin only)
   */
  async cleanupExpiredNotifications(): Promise<void> {
    await apiService.delete("/notifications/cleanup");
  }
}

export const notificationService = new NotificationService();
export default notificationService;
